import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ADMIN_API_URL } from "../../constants";
import showToast from '../../utils/toast';

const actionTypeColors = {
    CREATE_USER: '#28a745', // Green
    CREATE_ROLE: '#28a745', // Green
    CREATE_LEAD: '#28a745', // Green
    CREATE_CUSTOMER: '#28a745', // Green
    UPLOAD_KYC_DOCUMENT: '#28a745', // Green
    ADD_NOTE: '#28a745', // Green
    LOGIN: '#28a745', // Green

    UPDATE_USER: '#007bff', // Blue
    UPDATE_ROLE: '#007bff', // Blue
    UPDATE_LEAD: '#007bff', // Blue
    CHANGE_LEAD_STATUS: '#007bff', // Blue
    ASSIGN_LEAD: '#007bff', // Blue
    UPDATE_CUSTOMER: '#007bff', // Blue
    VERIFY_KYC_DOCUMENT: '#007bff', // Blue
    APPROVE_APPLICATION: '#007bff', // Blue
    REJECT_APPLICATION: '#007bff', // Blue

    DELETE_USER: '#dc3545', // Red
    DELETE_ROLE: '#dc3545', // Red
    DELETE_CUSTOMER: '#dc3545', // Red
    DELETE_LEAD: '#dc3545', // Red
};

const AuditLogsPage = ({ addNotification }) => {
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]); // State for dynamic user dropdown
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const tokenExpirationHandledRef = useRef(false);

    // Helper function to check for token expiration
    const handleTokenExpiration = (error) => {
        // If already handled, skip to avoid duplicate notifications (synchronous check with useRef)
        if (tokenExpirationHandledRef.current) {
            return true;
        }

        if (error && error.name === "TokenExpiredError" || 
            (error.message && error.message.includes("jwt expired")) ||
            (typeof error === 'object' && error.message && error.message.name === "TokenExpiredError")) {
            // Mark as handled immediately (synchronous) to prevent duplicate calls
            tokenExpirationHandledRef.current = true;
            showToast("Session expired. Please login again.", 'warning');
            addNotification("Session expired. Please login again.", 'warning');
            // Clear session storage
            sessionStorage.removeItem("authToken");
            sessionStorage.removeItem("userInfo");
            // Redirect to login
            setTimeout(() => {
                navigate("/");
            }, 2000);
            return true;
        }
        return false;
    };
    const [filters, setFilters] = useState({
        userId: '',
        actionType: '',
        startDate: '',
        endDate: '',
        sortBy: 'timestamp',
        sortOrder: 'DESC',
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            navigate("/");
            return;
        }
        fetchUsers(); // Fetch users for the filter dropdown
        fetchAuditLogs();
    }, [currentPage, recordsPerPage, filters.userId, filters.actionType, filters.startDate, filters.endDate, filters.sortBy, filters.sortOrder, navigate]);

    // Fetch audit logs data
    const fetchAuditLogs = async () => {
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            setLoading(false);
            return;
        }

        try {
            const queryParams = new URLSearchParams({
                page: currentPage,
                limit: recordsPerPage,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            });

            if (filters.userId) queryParams.append('userId', filters.userId);
            if (filters.actionType) queryParams.append('actionType', filters.actionType);
            if (filters.startDate) queryParams.append('startDate', filters.startDate);
            if (filters.endDate) queryParams.append('endDate', filters.endDate);

            const response = await fetch(`${ADMIN_API_URL}audit-logs?${queryParams.toString()}`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`,
                },
            });

            const result = await response.json();
            
            // Check for token expiration
            if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
                if (handleTokenExpiration(result.message)) {
                    setLoading(false);
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(result.message || "Failed to fetch audit logs.");
            }
            console.log("Audit Logs API response:", result);
            setLogs(result.logs || []);
            setTotalRecords(result.pagination ? result.pagination.total : 0);
            setTotalPages(result.pagination ? result.pagination.totalPages : 0);
        } catch (err) {
            // Check for token expiration in error
            if (handleTokenExpiration(err)) {
                setLoading(false);
                return;
            }
            setError(err.message);
            addNotification(`Error fetching audit logs: ${err.message}`, 'error');
            setLogs([]);
            setTotalRecords(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    // Fetch users for the filter dropdown
    const fetchUsers = async () => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            // Notification already handled in fetchAuditLogs, no need to repeat
            return;
        }
        try {
            const response = await fetch(`${ADMIN_API_URL}users`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`,
                },
            });

            const result = await response.json();
            
            // Check for token expiration
            if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
                if (handleTokenExpiration(result.message)) {
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(result.message || "Failed to fetch users for filter.");
            }
            setUsers(result.data);
        } catch (err) {
            // Check for token expiration in error
            if (handleTokenExpiration(err)) {
                return;
            }
            addNotification(`Error fetching users for filter: ${err.message}`, 'error');
            console.error("Error fetching users for filter:", err);
            setUsers([]);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prevFilters => ({
            ...prevFilters,
            [name]: value
        }));
        setCurrentPage(1); // Reset to first page on filter change
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleRecordsPerPageChange = (e) => {
        setRecordsPerPage(Number(e.target.value));
        setCurrentPage(1); // Reset to first page when records per page changes
    };

    const handleClearFilters = () => {
        setFilters({
            userId: '',
            actionType: '',
            startDate: '',
            endDate: '',
            sortBy: 'timestamp',
            sortOrder: 'DESC',
        });
        setCurrentPage(1);
        setRecordsPerPage(10);
    };

    const formatDisplayDateTime = (isoString) => {
        if (!isoString) return 'N/A';
        try {
            const date = new Date(isoString);
            // Format as DD MMM YYYY, HH:mm:ss
            const options = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
            return date.toLocaleDateString('en-GB', options).replace(/,/, ''); // Remove comma if present
        } catch (e) {
            console.error("Error formatting date:", isoString, e);
            return 'Invalid Date';
        }
    };

    const getActionTypeStyle = (actionType) => {
        const color = actionTypeColors[actionType];
        return color ? { color, fontWeight: 'bold' } : {};
    };

    return (
        <>
            <Header />
            <div id="wrapper" style={{ paddingTop: '70px' }}>
                <div className="content">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    <h2 className="font-light m-b-xs">Audit Logs / Activity Trails</h2>
                                    <form className="form-horizontal">
                                        <div className="form-group">
                                            <div className="col-sm-3">
                                                User:
                                                <select
                                                    className="form-control"
                                                    name="userId"
                                                    value={filters.userId}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="">All Users</option>
                                                    {users.map(user => <option key={user.id} value={user.id}>{user.username || user.name || user.email}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-sm-3">
                                                Action Type:
                                                <select
                                                    className="form-control"
                                                    name="actionType"
                                                    value={filters.actionType}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="">All Action Types</option>
                                                    {Object.keys(actionTypeColors).map(type => <option key={type} value={type}>{type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-sm-3">
                                                Start Date:
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="startDate"
                                                    value={filters.startDate}
                                                    onChange={handleFilterChange}
                                                />
                                            </div>
                                            <div className="col-sm-3">
                                                End Date:
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="endDate"
                                                    value={filters.endDate}
                                                    onChange={handleFilterChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <div className="col-sm-12 text-right">
                                                <button type="button" className="btn btn-default" onClick={handleClearFilters}>Clear Filters</button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    {loading && <p>Loading audit logs...</p>}
                                    {error && <p className="text-danger">Error: {error}</p>}
                                    {!loading && !error && logs.length === 0 && (
                                        <p className="text-center">No audit logs found for the selected criteria.</p>
                                    )}
                                    <div className="table-responsive" style={{ overflowX: 'auto' }}>
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>User</th>
                                                    <th>Timestamp</th>
                                                    <th>Action Type</th>
                                                    <th>Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {logs.map(log => (
                                                    <tr key={log.id}>
                                                        <td>{log.id.substring(0, 8)}...</td>
                                                        <td>{log.username || log.userId || 'N/A'}</td>
                                                        <td>{formatDisplayDateTime(log.timestamp)}</td>
                                                        <td style={getActionTypeStyle(log.actionType)}>{log.actionType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || 'N/A'}</td>
                                                        <td>{log.details || 'N/A'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination Controls */}
                                    <div className="text-right m-t-md">
                                        <label htmlFor="recordsPerPage" className="m-r-sm">Records per page:</label>
                                        <select
                                            id="recordsPerPage"
                                            className="form-control input-sm"
                                            style={{ display: 'inline-block', width: 'unset' }}
                                            value={recordsPerPage}
                                            onChange={handleRecordsPerPageChange}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <button
                                            className="btn btn-default m-r-sm m-l-sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                        <span>Page {currentPage} of {totalPages}</span>
                                        <button
                                            className="btn btn-default m-l-sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};

export default AuditLogsPage; 